import type { ReactNode } from 'react';
import { interactionIdOf, type ChatUi, type ChatUiKind } from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from './shared/ui/action.js';
import type { GenUiEntry, GenUiPolicy, GenUiSlot } from './shared/ui/registry.js';
// O pedaço de cada domínio no `GenUiConfig` (#5228): o núcleo não conhece os
// kinds, e cada domínio estende `GenUiConfig`/`GenUiPorts` no seu arquivo. Esta
// raiz agrega, como faz com o `REGISTRY`: os imports põem os pedaços no
// programa de quem só importa esta entrada (são só tipos, nada roda).
import './flight/shared/genui_config.js';
import './hotel/shared/genui_config.js';
import './rental/shared/genui_config.js';
import './bus/shared/genui_config.js';
import './mobility/shared/genui_config.js';
import './places/shared/genui_config.js';
import './business/product_checkout/genui_config.js';
import { flightOffersEntry } from './flight/flight_offers/index.js';
import { flightMultiOffersEntry } from './flight/flight_multi_offers/index.js';
import { flightCheaperDatesEntry } from './flight/flight_cheaper_dates/index.js';
import { flightPlannerEntry } from './flight/flight_planner/index.js';
import { flightSearchConfirmationEntry } from './flight/flight_search_confirmation/index.js';
import { flightReservationEntry } from './flight/flight_reservation/index.js';
import {
  busNextServicesEntry,
  flightNextServicesEntry,
  hotelNextServicesEntry,
  mobilityNextServicesEntry,
  rentalNextServicesEntry,
} from './business/next_services/index.js';
import { productCheckoutEntry } from './business/product_checkout/index.js';
import {
  approvalRequestEntry,
  approvalResultEntry,
  approvalBulkResultEntry,
} from './approval/approval/index.js';
import { approvalStackEntry } from './approval/approval_stack/index.js';
import { voucherCancellationEntry } from './voucher/voucher_cancellation/index.js';
import { voucherCancellationBulkEntry } from './voucher/voucher_cancellation_bulk/index.js';
import { rentalOffersEntry } from './rental/rental_offers/index.js';
import { rentalPlannerEntry } from './rental/rental_planner/index.js';
import { rentalPriceChangeEntry } from './rental/rental_price_change/index.js';
import { hotelPlannerEntry } from './hotel/hotel_planner/index.js';
import { hotelRoomsEntry } from './hotel/hotel_rooms/index.js';
import { consultPlacesEntry } from './places/consult_places/index.js';
import { hotelRateWarningEntry } from './hotel/hotel_rate_warning/index.js';
import { mobilityOffersEntry } from './mobility/mobility_offers/index.js';
import { checkoutOutcomeEntry } from './business/checkout_outcome/index.js';
import { mobilityPlannerEntry } from './mobility/mobility_planner/index.js';
import { mobilityRideDetailsEntry } from './mobility/mobility_ride_details/index.js';
import { ride99LinkEntry } from './mobility/ride99_link/index.js';
import { busOffersEntry } from './bus/bus_offers/index.js';
import { busStopsEntry } from './bus/bus_stops/index.js';
import { busPlannerEntry } from './bus/bus_planner/index.js';
import { busSeatMapEntry } from './bus/bus_seat_map/index.js';
import { tripsEntry } from './trips/trips/index.js';
import { flightSeatLinkEntry } from './trips/flight_seat_link/index.js';
import { guidedFlowsEntry } from './common/guided_flows/index.js';
import { walletLinkEntry } from './common/wallet_link/index.js';
import { confirmPromptEntry } from './common/confirm_prompt/index.js';
import { questionPromptEntry } from './common/question_prompt/index.js';
import { eventPremisesEntry } from './events/event_premises/index.js';

/**
 * `@voll-intelligence/genui-kit/react` — entrada React do contrato e fonte única
 * de renderização da UI generativa do canal app (modelo A2UI). Cada component
 * vive na sua pasta auto-contida e exporta UMA entry de catálogo (`{ kind, slot,
 * render }`); este arquivo só agrega as entries no `REGISTRY` e expõe o
 * dispatcher.
 *
 *  - `REGISTRY`: `kind → { slot, render }`. `1 kind ⇒ 1 render`, sem `switch`.
 *  - `ChatUiView`: renderiza UM component via `REGISTRY[kind].render`. A ação
 *    chega/sai por `dispatch` (action model) — o component é dono do texto; o
 *    shell é só transporte. Freeze por-card (readOnly + interactionId resolvido).
 *  - `slotOf`: o `slot` de um kind, pro shell rotear a lista sem conhecer kinds.
 *
 * O acoplamento com o shell (proxy de imagem) é injetado por Context
 * (`GenUiConfigProvider`); ícones e CSS são do próprio pacote.
 */

// Variância: cada entry é tipada no seu bloco específico (segurança na autoria);
// o REGISTRY as agrega já alargadas — o dispatcher garante kind↔render por chave.
const ENTRIES = [
  flightOffersEntry,
  flightMultiOffersEntry,
  flightCheaperDatesEntry,
  flightPlannerEntry,
  rentalOffersEntry,
  rentalPlannerEntry,
  rentalPriceChangeEntry,
  rentalNextServicesEntry,
  hotelPlannerEntry,
  hotelRoomsEntry,
  consultPlacesEntry,
  hotelRateWarningEntry,
  hotelNextServicesEntry,
  mobilityOffersEntry,
  mobilityNextServicesEntry,
  mobilityPlannerEntry,
  mobilityRideDetailsEntry,
  ride99LinkEntry,
  flightSearchConfirmationEntry,
  flightReservationEntry,
  flightNextServicesEntry,
  productCheckoutEntry,
  checkoutOutcomeEntry,
  approvalRequestEntry,
  approvalResultEntry,
  approvalStackEntry,
  approvalBulkResultEntry,
  voucherCancellationEntry,
  voucherCancellationBulkEntry,
  busOffersEntry,
  busStopsEntry,
  busPlannerEntry,
  busNextServicesEntry,
  busSeatMapEntry,
  tripsEntry,
  flightSeatLinkEntry,
  guidedFlowsEntry,
  walletLinkEntry,
  confirmPromptEntry,
  questionPromptEntry,
  eventPremisesEntry,
] as unknown as GenUiEntry[];

export const REGISTRY = Object.fromEntries(ENTRIES.map((e) => [e.kind, e])) as Record<
  ChatUiKind,
  GenUiEntry
>;

/** O slot de um kind (default `chat`). O shell roteia a lista por aqui. */
export function slotOf(kind: ChatUiKind): GenUiSlot {
  return REGISTRY[kind].slot;
}

const DEFAULT_POLICY: GenUiPolicy = {
  reconcileById: true,
  previewOverride: false,
  noteOnOverride: true,
};

/**
 * A política de thread de um kind, com defaults aplicados (irmã do `slotOf`).
 * O shell consulta por aqui — reconciliação, override por READ e congelamento
 * são declarados na entry de cada card, nunca por `if (kind === ...)` no shell.
 */
export function policyOf(kind: ChatUiKind): GenUiPolicy {
  return { ...DEFAULT_POLICY, ...REGISTRY[kind].policy };
}

export interface ChatUiViewProps {
  ui: ChatUi;
  note?: string;
  readOnly?: boolean;
  /**
   * Há turno mais novo depois deste card — o shell já andou. Não congela (isso é
   * `readOnly`): só avisa o card de que ele não é mais o último da conversa.
   */
  superseded?: boolean;
  /**
   * interactionIds já resolvidos server-side (Fase 2). Um card interativo cujo
   * interactionId está aqui renderiza somente-leitura mesmo sem `readOnly` de
   * conversa — congela por-card, à prova de reload.
   */
  resolvedInteractionIds?: ReadonlySet<string>;
  /**
   * Turno em voo para ESTE card — o shell está (re)computando o bloco. Repassado
   * ao render como `busy`; só cards com carregamento próprio (ex.: `flight_planner`)
   * o consomem, o resto ignora.
   */
  busy?: boolean;
  /**
   * Bloco remontado do histórico (não da emissão ao vivo do turno) — ver
   * `GenUiRenderProps.replay`. O shell sabe por qual pista a mensagem chegou;
   * o card decide o que é gesto de turno e o que é cena.
   */
  replay?: boolean;
  /** Canal genérico de ação — o component monta a própria action + texto. */
  dispatch: GenUiDispatch;
}

export function ChatUiView({
  ui,
  note,
  readOnly = false,
  superseded = false,
  resolvedInteractionIds,
  busy,
  replay = false,
  dispatch,
}: ChatUiViewProps): ReactNode {
  const entry = REGISTRY[ui.kind];
  // Congelamento por-card: conversa encerrada (`readOnly`) OU interactionId já
  // resolvido server-side. Os components escondem o CTA quando `readOnly`.
  const interactionId = interactionIdOf(ui);
  const resolved =
    interactionId !== undefined && (resolvedInteractionIds?.has(interactionId) ?? false);
  const ro = readOnly || resolved;
  return entry.render({ ui, note, readOnly: ro, superseded, busy, replay, dispatch });
}

// Re-exports da superfície pública da entrada React.
export { CATALOG } from '@voll-intelligence/genui-contract';
export type {
  GenUiAction,
  GenUiActionAnalytics,
  GenUiDispatch,
  FlightDetailSummary,
} from './shared/ui/action.js';
export type { GenUiEntry, GenUiPolicy, GenUiRenderProps, GenUiSlot } from './shared/ui/registry.js';
// A moeda sem centavos dos cards de resultado (voo, hotel, carro): o web-chat formata
// a página de carro pela mesma régua da conversa. Sai por `./react`, nunca pela raiz:
// `flight_format.ts` importa o intelligence-ui-kit, que as imagens de backend que leem
// a raiz (modal-session, conversation, analytics) não instalam — exportado pela raiz,
// derrubou o boot dos três em dev (ERR_MODULE_NOT_FOUND).
export { fmtMoneyWhole } from './shared/ui/flight_format.js';
// O explorer de voo mora no intelligence-ui-kit (#4997): a bancada do web-chat monta a
// `FlightExplorerPage` de lá com o ambiente montado da `GenUiConfig` dela, sem o
// provider em volta.
export { flightExplorerEnvOf } from './flight/shared/flight_results_env.js';
// O checkout mora no intelligence-ui-kit (#5000): a bancada do web-chat monta o
// `CheckoutDrawer` de lá com o ambiente montado da `GenUiConfig` dela, e devolve ao
// próprio BFF o envio no fio que os builders daqui montam. Os textos também saem
// daqui: as telas do host que reusam os campos do checkout leem os mesmos.
export { checkoutEnvOf, useCheckoutEnv } from './business/product_checkout/core/checkout_env.js';
export {
  conciergeCheckoutSubmit,
  offerCheckoutAction,
} from './business/product_checkout/ui/checkout/checkout_drawer.js';
export { checkoutCopy } from './business/product_checkout/ui/checkout/checkout_copy.js';
// Os textos do `PlannerForm` (`copy`): a página do web-chat os monta com o locale do app.
export { flightPlannerCopy } from './flight/shared/ui/flight_planner_copy.js';
export {
  GenUiConfigProvider,
  useGenUiConfig,
  type GenUiConfig,
  type GenUiOfflineFlightContext,
  type GenUiOfflineRequestPort,
  type GenUiTakeoverVertical,
} from './shared/ui/config.js';
export type { HotelSearchAreaStatus } from './hotel/shared/genui_config.js';
/** O único caminho de cópia do produto (Clipboard API → `execCommand`): o embed
 *  nega a Clipboard API e o kit não pode importar do host, então o helper mora
 *  aqui e trip-panel/web-chat re-exportam (#2976). */
export { copyPaths, copyText } from './shared/ui/clipboard.js';
export type { GenUiTelemetryPort } from './shared/ui/telemetry.js';
export { genUiT, resolveGenUiLocale, useGenUiI18n, type GenUiLocale } from './shared/ui/i18n.js';
/** Os motivos do aviso de tarifa em texto humano — o host da bancada
 *  determinística mostra a MESMA lista fora de um `GenUiConfigProvider`. */
export { hotelRateWarningReasons } from './hotel/hotel_rate_warning/hotel_rate_warning_reasons.js';
// Port de detalhe da OFERTA (drawer não-checkout "Detalhe do voo") — `FlightLeg`
// canônico vive aqui.
export type { FlightOffersPort, FlightLeg } from './flight/shared/flight_offers_port.js';
// Port de mapa de assentos da cabine — injetado via `ports.flightSeatMap`.
export type {
  FlightSeatMapPort,
  FlightSeatMapQuery,
  FlightSeatMapView,
  FlightSeatMapUnavailable,
  FlightSeatMapRow,
  FlightSeatMapSeat,
  FlightSeatMapColumn,
} from './flight/shared/flight_seat_map_port.js';
// Enriquecimento Cirium do trecho dos CARDS (SegmentDetailDrawer), por identidade
// do voo — o port que o web-chat injeta via `ports.flightSegmentEnrichment`.
export type {
  FlightSegmentEnrichment,
  FlightSegmentEnrichmentPort,
  FlightSegmentEnrichmentQuery,
} from './shared/ui/flight_enrichment.js';
export type { FlightTariffPort } from './flight/shared/flight_tariff_port.js';
export type { FlightPlannerPort } from './flight/shared/flight_planner_port.js';
export {
  useMobilityPlacesPort,
  type MobilityPlacesPort,
} from './mobility/shared/mobility_places_port.js';
export type { FlightAirportHistoryPort } from '@voll-intelligence/genui-contract';
export type { OneClickPort, LoyaltyPort, LoyaltyPlans } from './business/product_checkout/port.js';
export type { GenUiPorts } from './shared/ui/config.js';
export type { CheckoutOutcome } from './business/product_checkout/core/use_one_click_checkout.js';
export type { HotelSearchSharePort } from './hotel/shared/hotel_search_share_port.js';
export { flightQuoteRows } from './flight/shared/explorer/flight_quote_rows.js';
export type { HotelPageShare } from './hotel/hotel_planner/ui/hotel_results_page_copy.js';
// `resolveHotelRateDetail`/`HotelRateSelectionDetail` saem porque o seam
// determinístico (`HotelRateSelection.detail`, `use_hotel_details_view.ts`) os
// entrega ao host, e a bancada do web-chat chama a FUNÇÃO direto para nomear o
// quarto pela MESMA regra canônica em vez de manter uma cópia local.
export {
  resolveHotelRateDetail,
  type HotelRateSelectionDetail,
} from './hotel/shared/hotel_rooms_view.js';
// Organismo de resultados de hotel montado FORA de card: o passo de busca da
// bancada determinística do web-chat (`stage="search"`, submit explícito).
// O formulário do passo de busca da bancada, com tudo do host por prop (sem Context).
export { HotelDeterministicSearch } from './hotel/hotel_planner/ui/hotel_deterministic_search.js';
// A página de resultados da bancada, com tudo do host por prop (sem Context).
export {
  HotelDeterministicResultsPage,
  type HotelDeterministicResultsConfig,
} from './hotel/hotel_planner/ui/hotel_deterministic_results_page.js';
// A página de resultados enquanto a busca inicial da bancada corre (A6).
export { HotelSearchingResultsPage } from './hotel/hotel_planner/ui/hotel_results_page.js';
// A cena da lista (filtros + ordem) que a bancada leva à aba nova e traz de volta.
export type { HotelPanelQueryPort } from './hotel/hotel_planner/ui/planner_panel_list.js';
export {
  EMPTY_HOTEL_PANEL_QUERY,
  type HotelPanelQuery,
} from './hotel/hotel_planner/ui/use_hotel_panel_list.js';
// O recorte de tarifa da cena da lista, no vocabulário dos quartos (#5820): a
// aba nova abre a Página do Hotel com os chips da lista de onde veio.
export { listRateFilters } from './hotel/hotel_planner/ui/hotel_results_handlers.js';
export type { HotelRateSelection } from './hotel/hotel_planner/ui/hotel_details_drawer.js';
export { HotelDetailsPage } from './hotel/hotel_planner/ui/hotel_details_page.js';
// A fileira da estadia da Página do Hotel (Quem·Quando + a máquina do
// protótipo) — o host monta a área no slot `stayArea` da página e decide o que
// fazer com cada commit (busca ancorada / re-estadia).
export {
  HotelStayArea,
  useHotelStayEditor,
  type CommittedHotelStay,
} from './hotel/hotel_planner/ui/hotel_stay_area.js';
// O estado do CORAÇÃO (item 1 F2), UMA instância por bancada — vive no HOST
// porque a lista e a Página do Hotel são irmãs na árvore dele: é o host que
// liga o mesmo estado nas duas (`favorites.apply` nas ofertas da lista,
// `favorite` na página). O IO é a porta `ports.hotelFavorites`, que o host passa
// por argumento.
export {
  useHotelFavorites,
  type HotelFavorites,
} from './hotel/hotel_planner/ui/use_hotel_favorites.js';
export type { HotelFavoritesPort } from '@voll-intelligence/genui-contract';
// O cache de leitura da gaveta é criado e descartado pelo HOST (uma instância por
// jornada), então sai junto — sem ele a gaveta pública não é montável.
export {
  createHotelReadCache,
  disposeHotelReadCache,
  patchHotelDetailsRate,
  primeHotelDetails,
  type HotelDetailsRatePatch,
  type HotelReadCache,
} from './hotel/hotel_planner/ui/hotel_read_cache.js';
